import { capitalizeString } from '@ltvco/refresh-lib/utils';

import type { PostIndi } from '@ltvco/refresh-lib/fmt';
import type { serializedTypes } from '@ltvco/refresh-lib/v1';

const normalizeNameOnCapitalization = (name: string | null | undefined) => {
  if (!name) {
    return null;
  }
  if (name !== name?.toUpperCase()) {
    return name;
  }

  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
};

export const formatFamilyTreeDataFromPersonReport = (
  person: serializedTypes.SerializedPerson | serializedTypes.Connection,
  permalink: string | null,
  bvid: string | null,
  address?:
    | { city: string | null; state: string | null; country: string | null }
    | null
    | {}
) => {
  const names = person.identity.names?.[0]?.parsed;
  const dob = person.identity.dobs?.[0]?.date?.parsed;
  const dod = person.identity.dods?.[0]?.date?.parsed;
  let profile_picture = null;

  if ('images' in person) {
    profile_picture = person.images?.[0]?.url;
  }

  const firstName = normalizeNameOnCapitalization(names?.first);
  const middleName = normalizeNameOnCapitalization(names?.middle);
  const lastName = normalizeNameOnCapitalization(names?.last);

  return {
    first_name: firstName,
    middle_name: middleName,
    last_name: lastName,
    dob_month: dob?.month,
    dob_year: dob?.year,
    tokenized_dob_day: dob?.day,
    dod_month: dod?.month,
    dod_year: dod?.year,
    tokenized_dod_day: dod?.day,
    relationship: null,
    preparedFrom: '',
    placeholder: false,
    profile_picture,
    person_report_params: bvid
      ? {
          bvid,
          relatives_expanded: false,
          permalink,
          place:
            address && 'city' in address && Boolean(address.city?.trim())
              ? {
                  city: address.city ?? undefined,
                  state: address.state ?? undefined,
                  country: address.country ?? undefined,
                }
              : undefined,
        }
      : undefined,
  };
};

export const formatPostIndiFromConnection = (
  person: serializedTypes.SerializedPerson | serializedTypes.Connection,
  address?:
    | { city: string | null; state: string | null; country: string | null }
    | null
    | {}
): PostIndi | null => {
  const names = person.identity.names?.[0]?.parsed;
  const dob = person.identity.dobs?.[0]?.date?.parsed;
  const dod = person.identity.dods?.[0]?.date?.parsed;

  if (!person.identity) {
    return null;
  }

  const firstName = normalizeNameOnCapitalization(names?.first);
  const middleName = normalizeNameOnCapitalization(names?.middle);
  const lastName = normalizeNameOnCapitalization(names?.last);

  // Handle address formatting
  const city = address && 'city' in address ? address.city || '' : '';
  const state = address && 'state' in address ? address.state || '' : '';

  return {
    first_name: capitalizeString(firstName || ''),
    middle_name: capitalizeString(middleName || ''),
    last_name: capitalizeString(lastName || ''),
    dob_month: dob?.month,
    dob_year: dob?.year,
    dod_month: dod?.month,
    dod_year: dod?.year,
    place: {
      street_address_1: '',
      city,
      state,
      zip_code: '',
    },
  };
};
